import { Component, computed, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationItem } from '@libs/core/notification/responses/notification.response';
import { NotificationType } from '@libs/core/notification/enum/notification-type.enum';
import { TranslateService } from '@ngx-translate/core';
import { escapeHtml } from '@core/utils/escape-html';
import { MonogramPipe, PmMonogramCircleComponent } from '@libs/ui';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

const HEADLINE_TEMPLATE_KEYS: Record<NotificationType, string> = {
  [NotificationType.COMMENT_MENTION]: 'NOTIFICATION.MENTIONED_YOU_IN_COMMENT',
  [NotificationType.COMMENT_REPLY]: 'NOTIFICATION.REPLIED_TO_YOUR_COMMENT',
  [NotificationType.COMMENT_THREAD_REPLY]: 'NOTIFICATION.REPLIED_IN_THREAD',
};

@Component({
  selector: 'pm-notification-item',
  imports: [PmMonogramCircleComponent, MonogramPipe],
  template: `
    <button
      safeButtonUsage
      class="w-full text-left flex flex-col justify-center items-start gap-1 self-stretch px-5 py-3 border-b border-gray-100 last:border-b-0"
      [class.bg-[#EBF4FF]]="!notification().isRead"
      [class.hover:bg-surface-interactive-hover]="notification().isRead"
      (click)="onNotificationClick()"
    >
      <div class="flex items-start gap-3 w-full">
        <pm-monogram-circle
          class="mt-0.5"
          [initials]="actorNameParts() | monogram"
        />
        <div class="flex-1 min-w-0 flex flex-col gap-1">
          <div class="flex items-baseline justify-between gap-2">
            <p
              class="text-sm text-content-title leading-snug line-clamp-2"
              [innerHTML]="headline()"
            ></p>
            <span
              class="text-[11px] text-content-subdued whitespace-nowrap flex-shrink-0"
            >
              {{ getTimeAgo() }}
            </span>
          </div>
          @if (notification().referenceId) {
            <p class="text-xs text-content-default truncate">
              {{ notification().referenceId }}
            </p>
          }
          @if (notification().description) {
            <p class="text-xs text-content-default line-clamp-2">
              {{ notification().description }}
            </p>
          }
        </div>
      </div>
    </button>
  `,
})
export class NotificationItemComponent {
  notification = input.required<NotificationItem>();
  clicked = output<void>();

  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  actorNameParts = computed(() =>
    (this.notification().actorName ?? '').trim().split(/\s+/).slice(0, 2),
  );

  headline = computed(() => {
    const n = this.notification();
    const actorName = n.actorName ?? '';
    const templateKey = HEADLINE_TEMPLATE_KEYS[n.type] ?? '';
    const translated = templateKey ? this.translate.instant(templateKey) : '';
    const boldActor = `<strong>${escapeHtml(actorName)}</strong>`;
    return translated.replace('{actor}', boldActor);
  });

  getTimeAgo(): string {
    const lang = this.translate.getCurrentLang();
    const locale = lang?.startsWith('de') ? de : undefined;
    return formatDistanceToNow(new Date(this.notification().createdAt), {
      addSuffix: true,
      locale,
    });
  }

  onNotificationClick(): void {
    this.clicked.emit();

    const n = this.notification();
    const url = n.navigationUrl;
    if (!url) {
      return;
    }

    // Pass path segments (not a whole url) so Angular's serializer encodes each
    // one; otherwise an unencoded material id like "Dual Clutch Transmission
    // (DCT)" is parsed as auxiliary-outlet syntax and silently dropped.
    const segments = url.split('/').filter(Boolean);
    this.router.navigate(['/', ...segments], {
      fragment: n.sourceId ? `comment-${n.sourceId}` : undefined,
    });
  }
}
